import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast, Toaster } from "react-hot-toast";
import { apiCall } from "utils/apiClient";
import PropTypes from "prop-types";
import Pagination from "components/pagination";
import { addNotificationHistory, updateNotificationHistory } from "../utils/historyStorage";
import {
  getMockNotificationDraft,
  getMockRecipients,
  USE_NOTIFICATION_DEMO_DATA,
} from "../utils/mockData";

const AUDIENCE_API = {
  customer: {
    endpoint: "customer/getCustomer",
    method: "POST",
    buildPayload: () => ({ page: 1, limit: 1_000 }),
  },
  partner: {
    endpoint: "partner/list",
    method: "POST",
    buildPayload: () => ({ page: 1, limit: 1_000 }),
  },
};

const MODULES = [
  { value: "customer", label: "Customer" },
  { value: "partner", label: "Partner" },
];

const TARGET_OPTIONS = ["Campaign", "Event", "Offer", "Reminder", "Announcement"];

const ATTACHMENT_ACCEPT = "image/*,.pdf,.doc,.docx,.txt,.csv,.xls,.xlsx";

const normalizeText = (value) => String(value || "").trim();
const getPayloadData = (value) => (Array.isArray(value?.data) ? value.data : value?.data?.data || []);
const isImageFile = (file) => Boolean(file?.type?.startsWith("image/"));
const createAttachment = (file) => ({
  id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2, 7)}`,
  file,
  previewUrl: URL.createObjectURL(file),
  isImage: isImageFile(file),
});
const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "");
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
const revokeAttachment = (attachment) => {
  if (attachment?.previewUrl) {
    URL.revokeObjectURL(attachment.previewUrl);
  }
};

const isSuccess = (result) =>
  result?.status === 200 ||
  result?.status === 201 ||
  result?.success === true ||
  result?.status === "success";

const getRecipientId = (item) =>
  item?.id ||
  item?.customer_id ||
  item?.partner_id ||
  item?._id ||
  null;

const getRecipientName = (item) =>
  item?.name ||
  item?.full_name ||
  item?.customer_name ||
  item?.partner_name ||
  item?.email ||
  "Unknown";

const getRecipientMeta = (item) =>
  item?.mobile ||
  item?.phone ||
  item?.contact ||
  item?.email ||
  "-";

const NotificationComposer = ({
  title = "Notification",
  messageLabel = "Notification Message",
  channel,
  endpoint,
  messageLimit,
  submitLabel = "Send Notification",
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const editRecord = location.state?.editRecord;
  const isEditMode = Boolean(editRecord?.id);
  const initialDraft = USE_NOTIFICATION_DEMO_DATA ? getMockNotificationDraft(channel) : null;
  const [moduleType, setModuleType] = useState("customer");
  const [notificationTarget, setNotificationTarget] = useState(initialDraft?.target || "");
  const [notificationTitle, setNotificationTitle] = useState(initialDraft?.subject || "");
  const [notificationMessage, setNotificationMessage] = useState(initialDraft?.message || "");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [editingFileIndex, setEditingFileIndex] = useState(null);
  const [recipientList, setRecipientList] = useState([]);
  const [recipientError, setRecipientError] = useState("");
  const [recipientSearch, setRecipientSearch] = useState("");
  const [recipientPage, setRecipientPage] = useState(1);
  const [recipientRecordsPerPage, setRecipientRecordsPerPage] = useState(10);
  const [selectedRecipientIds, setSelectedRecipientIds] = useState([]);
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [loading, setLoading] = useState(false);

  const fileInputRef = useRef(null);
  const editFileInputRef = useRef(null);
  const exceedsLimit = messageLimit ? notificationMessage.length > messageLimit : false;

  const fetchRecipients = async (selectedModule) => {
    const config = AUDIENCE_API[selectedModule];
    if (!config) {
      setRecipientList([]);
      return;
    }

    setLoadingRecipients(true);
    setRecipientError("");

    if (USE_NOTIFICATION_DEMO_DATA) {
      const nextRecipients = getMockRecipients(selectedModule);
      const nextRecipientIds = nextRecipients.map((item) => getRecipientId(item)).filter(Boolean);
      setRecipientList(nextRecipients);
      setSelectedRecipientIds(nextRecipientIds);
      setLoadingRecipients(false);
      return;
    }

    try {
      const result = await apiCall({
        endpoint: config.endpoint,
        method: config.method,
        data: config.buildPayload(),
      });

      if (!isSuccess(result)) {
        throw new Error(result?.message || "Failed to fetch recipients");
      }

      const nextRecipients = getPayloadData(result);
      const nextRecipientIds = nextRecipients.map((item) => getRecipientId(item)).filter(Boolean);
      setRecipientList(nextRecipients);
      setSelectedRecipientIds(nextRecipientIds);
    } catch (error) {
      setRecipientList([]);
      setSelectedRecipientIds([]);
      setRecipientError(error?.message || "Failed to fetch recipients");
    } finally {
      setLoadingRecipients(false);
    }
  };

  useEffect(() => {
    fetchRecipients(moduleType);
  }, [moduleType]);

  useEffect(() => {
    setRecipientSearch("");
    setRecipientPage(1);
  }, [moduleType]);

  useEffect(() => {
    if (!isEditMode) {
      return;
    }

    setModuleType(editRecord?.module || "customer");
    setNotificationTarget(editRecord?.target || "");
    setNotificationTitle(editRecord?.subject || "");
    setNotificationMessage(editRecord?.message || "");
  }, [editRecord, isEditMode]);

  useEffect(() => {
    if (!isEditMode || !Array.isArray(editRecord?.recipients) || recipientList.length === 0) {
      return;
    }

    const availableIds = new Set(recipientList.map((item) => getRecipientId(item)).filter(Boolean));
    const nextSelectedIds = editRecord.recipients
      .map((recipient) => recipient?.id)
      .filter((id) => availableIds.has(id));

    setSelectedRecipientIds(nextSelectedIds);
  }, [editRecord, isEditMode, recipientList]);

  const filteredRecipients = useMemo(() => {
    const keyword = recipientSearch.trim().toLowerCase();
    if (!keyword) {
      return recipientList;
    }

    return recipientList.filter((item) => {
      const name = String(getRecipientName(item)).toLowerCase();
      const meta = String(getRecipientMeta(item)).toLowerCase();
      return name.includes(keyword) || meta.includes(keyword);
    });
  }, [recipientList, recipientSearch]);

  const allRecipientIds = useMemo(
    () => recipientList.map((item) => getRecipientId(item)).filter(Boolean),
    [recipientList]
  );

  const areAllRecipientsSelected = allRecipientIds.length > 0 &&
    allRecipientIds.every((id) => selectedRecipientIds.includes(id));

  const totalRecipientPages = Math.max(1, Math.ceil(filteredRecipients.length / recipientRecordsPerPage));

  useEffect(() => {
    if (recipientPage > totalRecipientPages) {
      setRecipientPage(totalRecipientPages);
    }
  }, [recipientPage, totalRecipientPages]);

  const paginatedRecipients = filteredRecipients.slice(
    (recipientPage - 1) * recipientRecordsPerPage,
    recipientPage * recipientRecordsPerPage
  );

  useEffect(
    () => () => {
      selectedFiles.forEach(revokeAttachment);
    },
    [selectedFiles]
  );

  const clearSelectedFiles = () => {
    setSelectedFiles((prev) => {
      prev.forEach(revokeAttachment);
      return [];
    });
    setEditingFileIndex(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    if (editFileInputRef.current) {
      editFileInputRef.current.value = "";
    }
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    setSelectedFiles((prev) => {
      prev.forEach(revokeAttachment);
      return files.map(createAttachment);
    });
  };

  const handleDeleteFile = (index) => {
    setSelectedFiles((prev) => {
      const nextFiles = [...prev];
      const [removed] = nextFiles.splice(index, 1);
      revokeAttachment(removed);
      return nextFiles;
    });
  };

  const handleEditFile = (index) => {
    setEditingFileIndex(index);
    if (editFileInputRef.current) {
      editFileInputRef.current.value = "";
      editFileInputRef.current.click();
    }
  };

  const handleEditFileChange = (event) => {
    const nextFile = event.target.files?.[0];
    if (!nextFile || editingFileIndex === null) {
      return;
    }

    setSelectedFiles((prev) => {
      if (!prev[editingFileIndex]) {
        return prev;
      }

      const nextFiles = [...prev];
      revokeAttachment(nextFiles[editingFileIndex]);
      nextFiles[editingFileIndex] = createAttachment(nextFile);
      return nextFiles;
    });

    setEditingFileIndex(null);
    if (editFileInputRef.current) {
      editFileInputRef.current.value = "";
    }
  };

  const handleToggleRecipient = (recipientId) => {
    if (!recipientId) {
      return;
    }
    setSelectedRecipientIds((prev) =>
      prev.includes(recipientId)
        ? prev.filter((id) => id !== recipientId)
        : [...prev, recipientId]
    );
  };

  const handleToggleSelectAllRecipients = () => {
    if (areAllRecipientsSelected) {
      setSelectedRecipientIds([]);
      return;
    }
    setSelectedRecipientIds(allRecipientIds);
  };

  const saveHistoryRecord = async ({ target, subject, body, recipientIds }) => {
    const selectedIdSet = new Set(recipientIds);
    const selectedRecipients = recipientList
      .filter((item) => selectedIdSet.has(getRecipientId(item)))
      .map((item) => ({
        id: getRecipientId(item),
        name: getRecipientName(item),
        meta: getRecipientMeta(item),
      }));

    const attachments = await Promise.all(
      selectedFiles.map(async (attachment) => {
        const fileType = attachment?.file?.type || "";
        const nextAttachment = {
          name: attachment?.file?.name || "Attachment",
          size: attachment?.file?.size || 0,
          type: fileType,
        };

        if (fileType.startsWith("image/")) {
          try {
            nextAttachment.previewDataUrl = await readFileAsDataUrl(attachment.file);
          } catch (error) {
            console.error("Attachment preview generation error:", error);
          }
        }

        return nextAttachment;
      })
    );

    const nextRecord = {
      channel,
      module: moduleType,
      target,
      subject,
      message: body,
      totalRecipients: recipientIds.length,
      recipients: selectedRecipients,
      attachments,
      endpoint,
      status: "sent",
      isDemo: USE_NOTIFICATION_DEMO_DATA,
    };

    if (isEditMode) {
      return updateNotificationHistory(editRecord.id, nextRecord);
    }

    return addNotificationHistory(nextRecord);
  };

  const resetComposer = () => {
    if (isEditMode) {
      navigate("/notification-list");
      return;
    }

    setNotificationTarget(initialDraft?.target || "");
    setNotificationTitle(initialDraft?.subject || "");
    setNotificationMessage(initialDraft?.message || "");
    clearSelectedFiles();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const subject = normalizeText(notificationTitle);
    const target = normalizeText(notificationTarget);
    const body = normalizeText(notificationMessage);

    if (!subject || !target || !body) {
      toast.error("Target, subject and message are required.");
      return;
    }

    if (messageLimit && body.length > messageLimit) {
      toast.error(`Message must be ${messageLimit} characters or less.`);
      return;
    }

    if (!recipientList.length) {
      toast.error(`No ${moduleType}s found in list.`);
      return;
    }

    const recipientIds = selectedRecipientIds.filter(Boolean);

    if (!recipientIds.length) {
      toast.error("Please select at least one recipient.");
      return;
    }

    const payload = {
      channel,
      module: moduleType,
      module_type: moduleType,
      audience: moduleType,
      title: subject,
      subject,
      target,
      message: body,
      body,
      recipient_ids: recipientIds,
      total_recipients: recipientIds.length || recipientList.length,
    };

    setLoading(true);
    try {
      if (USE_NOTIFICATION_DEMO_DATA) {
        await saveHistoryRecord({ target, subject, body, recipientIds });
        toast.success(isEditMode ? `${title} updated successfully.` : `${title} sent successfully in demo mode.`);
        resetComposer();
        return;
      }

      let requestData = payload;

      if (selectedFiles.length > 0) {
        const formData = new FormData();
        formData.append("channel", payload.channel);
        formData.append("module", payload.module);
        formData.append("module_type", payload.module_type);
        formData.append("audience", payload.audience);
        formData.append("title", payload.title);
        formData.append("subject", payload.subject);
        formData.append("target", payload.target);
        formData.append("message", payload.message);
        formData.append("body", payload.body);
        formData.append("recipient_ids", JSON.stringify(payload.recipient_ids));
        formData.append("total_recipients", String(payload.total_recipients));
        selectedFiles.forEach((attachment) => {
          formData.append("attachments", attachment.file);
        });
        requestData = formData;
      }

      const result = await apiCall({
        endpoint,
        method: "POST",
        data: requestData,
      });

      if (isSuccess(result)) {
        await saveHistoryRecord({ target, subject, body, recipientIds });

        toast.success(isEditMode ? `${title} updated successfully.` : `${title} sent successfully.`);
        resetComposer();
        return;
      }

      toast.error(result?.message || "Failed to send notification.");
    } catch (error) {
      console.error("Notification send error:", error);
      toast.error("Failed to send notification.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <Toaster position="top-right" reverseOrder={false} />

      {USE_NOTIFICATION_DEMO_DATA ? (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Demo data mode is active. Recipients, send flow, and notification history are running locally until the real API is connected.
        </div>
      ) : null}

      {isEditMode ? (
        <div className="mb-4 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
          Edit mode is active for this notification record. Saving will update the list entry.
        </div>
      ) : null}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="rounded-xl border border-slate-200 bg-gradient-to-r from-slate-50 to-blue-50 p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-sm font-semibold text-slate-800">{title} Delivery</p>
              <p className="text-xs text-slate-500">Pick module, select recipients, then send.</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600">
                {moduleType}
              </span>
              <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600">
                {selectedRecipientIds.length} selected
              </span>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3">
          <label className="mb-2 block text-sm font-semibold text-slate-700">Target Module</label>
          <div className="grid grid-cols-2 gap-2 sm:inline-flex sm:gap-0 sm:overflow-hidden sm:rounded-md sm:border sm:border-slate-200">
            {MODULES.map((item) => {
              const isActive = moduleType === item.value;
              return (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => setModuleType(item.value)}
                  className={`rounded-md border px-4 py-2 text-sm font-semibold transition-colors sm:rounded-none sm:border-0 ${
                    isActive
                      ? "border-slate-900 bg-slate-900 text-white shadow-sm"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 rounded-xl border border-slate-200 bg-white p-3 xl:grid-cols-12">
          <div className="xl:col-span-4">
            <label htmlFor={`${channel}-target`} className="mb-2 block text-sm font-semibold text-slate-700">
              Target
            </label>
            <input
              id={`${channel}-target`}
              list={`${channel}-target-options`}
              required
              value={notificationTarget}
              onChange={(event) => setNotificationTarget(event.target.value)}
              className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-xs outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
              placeholder="Select or type target (Campaign/Event)"
            />
            <datalist id={`${channel}-target-options`}>
              {TARGET_OPTIONS.map((option) => (
                <option key={option} value={option} />
              ))}
            </datalist>
            <p className="mt-1 text-[11px] text-slate-500">
              Use this for campaign/event tagging, or type custom target.
            </p>

            <div className="mt-3">
              <label className="mb-2 block text-sm font-semibold text-slate-700">Upload Files / Images</label>
              <div className="flex flex-wrap items-center gap-3">
                <label
                  htmlFor={`${channel}-attachments`}
                  className="cursor-pointer rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100"
                >
                  Upload
                </label>
                <input
                  ref={fileInputRef}
                  id={`${channel}-attachments`}
                  type="file"
                  multiple
                  accept={ATTACHMENT_ACCEPT}
                  onChange={handleFileChange}
                  className="hidden"
                />
                <input
                  ref={editFileInputRef}
                  type="file"
                  accept={ATTACHMENT_ACCEPT}
                  onChange={handleEditFileChange}
                  className="hidden"
                />
                <span className="text-xs text-slate-500">
                  {selectedFiles.length ? `${selectedFiles.length} file(s) selected` : "No file selected"}
                </span>
              </div>
              {selectedFiles.length > 0 ? (
                <div className="mt-2 flex flex-nowrap gap-2 overflow-x-auto pb-1">
                  {selectedFiles.map((attachment, index) => (
                    <div key={attachment.id} className="w-28 shrink-0 rounded-md border border-slate-200 bg-white p-1 shadow-sm">
                      <a
                        href={attachment.previewUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="block h-20 w-full overflow-hidden rounded-md bg-slate-50"
                      >
                        {attachment.isImage ? (
                          <img
                            src={attachment.previewUrl}
                            alt={attachment.file.name}
                            className="h-full w-full object-contain"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center px-2 text-center text-xs font-semibold text-slate-600">
                            Open File
                          </div>
                        )}
                      </a>

                      <div className="pt-1">
                        <p className="truncate text-[11px] font-semibold text-slate-800">{attachment.file.name}</p>
                        <p className="text-[10px] text-slate-500">{Math.max(1, Math.round(attachment.file.size / 1024))} KB</p>
                        <div className="mt-1 flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => handleEditFile(index)}
                            className="rounded border border-slate-300 px-1.5 py-0.5 text-[10px] font-semibold text-slate-700 hover:bg-slate-100"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteFile(index)}
                            className="rounded border border-red-200 px-1.5 py-0.5 text-[10px] font-semibold text-red-600 hover:bg-red-50"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>

          <div className="xl:col-span-2">
            <label htmlFor={`${channel}-subject`} className="mb-2 block text-sm font-semibold text-slate-700">
              Subject
            </label>
            <input
              id={`${channel}-subject`}
              required
              value={notificationTitle}
              onChange={(event) => setNotificationTitle(event.target.value)}
              className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-xs outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
              placeholder={`Enter ${title} subject`}
            />
          </div>

          <div className="xl:col-span-6">
            <label htmlFor={`${channel}-message`} className="mb-2 block text-sm font-semibold text-slate-700">
              {messageLabel}
            </label>
            <textarea
              id={`${channel}-message`}
              value={notificationMessage}
              required
              onChange={(event) => setNotificationMessage(event.target.value)}
              rows={8}
              className="h-44 w-full resize-none overflow-y-auto rounded-lg border border-slate-300 px-3 py-3 text-sm outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
              placeholder={`Type ${title.toLowerCase()} message`}
            />
            {messageLimit ? (
              <p className={`mt-1 text-xs ${exceedsLimit ? "text-red-500" : "text-slate-500"}`}>
                {notificationMessage.length}/{messageLimit} characters
              </p>
            ) : null}
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <label className="text-sm font-semibold text-slate-700">
              {moduleType === "customer" ? "Customer List" : "Partner List"}
            </label>
            <div className="flex flex-wrap items-center gap-2">
              <label className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-700">
                <input
                  type="checkbox"
                  checked={areAllRecipientsSelected}
                  onChange={handleToggleSelectAllRecipients}
                />
                Select All
              </label>
              <button
                type="button"
                onClick={() => setSelectedRecipientIds([])}
                className="rounded-md border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100"
              >
                Clear
              </button>
              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
                {selectedRecipientIds.length} selected
              </span>
              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
                {filteredRecipients.length}/{recipientList.length} records
              </span>
            </div>
          </div>
          <div className="mb-2">
            <input
              type="text"
              value={recipientSearch}
              onChange={(event) => {
                setRecipientSearch(event.target.value);
                setRecipientPage(1);
              }}
              className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-xs outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200 md:w-64"
              placeholder={`Search ${moduleType} list`}
            />
          </div>

          <div className="max-h-80 overflow-auto rounded-lg border border-slate-200 bg-white">
            {loadingRecipients ? (
              <p className="p-4 text-sm text-slate-500">Loading list...</p>
            ) : recipientError ? (
              <p className="p-4 text-sm text-red-500">{recipientError}</p>
            ) : filteredRecipients.length === 0 ? (
              <p className="p-4 text-sm text-slate-500">No records found.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {paginatedRecipients.map((item, index) => {
                  const recipientId = getRecipientId(item);
                  const isChecked = recipientId ? selectedRecipientIds.includes(recipientId) : false;
                  return (
                    <li key={recipientId || `${moduleType}-${index}`} className="px-4 py-2.5 hover:bg-slate-50">
                      <label className="flex cursor-pointer items-start gap-2">
                        <input
                          type="checkbox"
                          className="mt-0.5"
                          checked={isChecked}
                          disabled={!recipientId}
                          onChange={() => handleToggleRecipient(recipientId)}
                        />
                        <div>
                          <p className="text-sm font-medium text-slate-800">{getRecipientName(item)}</p>
                          <p className="text-xs text-slate-500">{getRecipientMeta(item)}</p>
                        </div>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          {filteredRecipients.length > 0 ? (
            <div className="mt-2 rounded-lg border border-slate-200 bg-white">
              <Pagination
                currentPage={recipientPage}
                totalPages={totalRecipientPages}
                onPageChange={(page) => setRecipientPage(page)}
                recordsPerPage={recipientRecordsPerPage}
                onRecordsPerPageChange={(value) => {
                  setRecipientPage(1);
                  setRecipientRecordsPerPage(value);
                }}
                totalRecords={filteredRecipients.length}
                count={paginatedRecipients.length}
                showFirstLastControls
                compact
              />
            </div>
          ) : null}
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading}
            className="rounded-lg bg-gradient-to-r from-slate-900 to-slate-700 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:from-slate-800 hover:to-slate-700 disabled:cursor-not-allowed disabled:from-slate-400 disabled:to-slate-400"
          >
            {loading ? (isEditMode ? "Updating..." : "Sending...") : (isEditMode ? `Update ${title} Notification` : submitLabel)}
          </button>
        </div>
      </form>
    </div>
  );
};

NotificationComposer.propTypes = {
  title: PropTypes.string,
  messageLabel: PropTypes.string,
  channel: PropTypes.string.isRequired,
  endpoint: PropTypes.string.isRequired,
  messageLimit: PropTypes.number,
  submitLabel: PropTypes.string,
};

NotificationComposer.defaultProps = {
  title: "Notification",
  messageLabel: "Notification Message",
  messageLimit: null,
  submitLabel: "Send Notification",
};

export default NotificationComposer;
